/**
 * The tab strip: the app's only navigation (docs/ux/review/navigation-ia.md §3.2, artboards
 * `optimal/` decision 1). Home is tab 0 (⌥0) with the global ◆/◑ totals; pinned projects follow
 * with the ⌘/⌥-number, a needs-you badge (◆) and a running badge (◑); drag or ⌥⇧←/→ to reorder; a
 * context menu (Move, Unpin, Project settings, Archive); an overflow `⋯` for the rest; `+` for a
 * new project; Search (⌘K) and Settings on the right. A badge opens Home filtered to its project.
 * Presentational: the container (components/shell/ShellTabs.tsx) owns data and writes.
 */
import { Link } from '@tanstack/react-router'
import { Button, Sheet, shortcutLabel } from '@viko/transcript-ui'
import { House, MoreHorizontal, Plus, Search, Settings } from 'lucide-react'
import {
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { homeHref, projectHref } from '../../lib/ia.js'
import { tabKeyShortcuts, tabShortcut } from '../../lib/shortcut.js'
import { placeOf } from '../../lib/tab-memory.js'
import { TAB_KEYS } from '../../lib/tab-order.js'
import { VMark, Wordmark } from '../brand.js'
import { Glyph } from '../lanes/glyphs.js'
import '../lanes/lanes.css'

export interface TabItem {
  id: string
  name: string
  needs: number
  running: number
}

export interface ProjectTabsProps {
  tabs: TabItem[]
  /** Active projects without a pinned slot (overflow menu, with Pin). */
  unpinned: { id: string; name: string }[]
  /** The current project tab, `home`, or `settings`. */
  current?: string | undefined
  standalone: boolean
  totals: { needs: number; running: number }
  onMove: (id: string, to: number) => void
  onUnpin: (id: string) => void
  onPin: (id: string) => void
  onArchive: (id: string) => void
  onAdd: () => void
  /** ⌘K. */
  onSearch: () => void
  /** "Locked · Unlock" (components/lock/ContentLock.tsx), right after Home. */
  lockChip?: ReactNode
  /** viko-call: the Call button (or On call), before Search. */
  callSlot?: ReactNode
}

export const HOME_TAB = 'home'
export const SETTINGS_TAB = 'settings'

export function ProjectTabs({
  tabs,
  unpinned,
  current,
  standalone,
  totals,
  onMove,
  onUnpin,
  onPin,
  onArchive,
  onAdd,
  onSearch,
  lockChip,
  callSlot,
}: ProjectTabsProps) {
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null)
  const [overflow, setOverflow] = useState<{ x: number; y: number } | null>(null)
  const [archive, setArchive] = useState<TabItem | null>(null)
  const [drag, setDrag] = useState<{ id: string; over?: number } | null>(null)
  const shown = tabs.slice(0, TAB_KEYS)
  const extra = tabs.slice(TAB_KEYS)
  const bar = useRef<HTMLElement>(null)

  const openMenu = (id: string, target: HTMLElement, e?: MouseEvent) => {
    const host = bar.current?.getBoundingClientRect()
    const r = target.getBoundingClientRect()
    setMenu({
      id,
      x: (e?.clientX ?? r.left) - (host?.left ?? 0),
      y: (e ? e.clientY : r.bottom) - (host?.top ?? 0) + 4,
    })
  }
  const onTabKey = (e: KeyboardEvent<HTMLElement>, id: string, index: number) => {
    if (e.altKey && e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault()
      const to = index + (e.key === 'ArrowLeft' ? -1 : 1)
      if (to >= 0 && to < tabs.length) {
        onMove(id, to)
        // Keep focus on the moved tab once it re-renders in its new slot.
        requestAnimationFrame(() =>
          bar.current?.querySelector<HTMLElement>(`[data-tab="${CSS.escape(id)}"]`)?.focus(),
        )
      }
      return
    }
    if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
      e.preventDefault()
      openMenu(id, e.currentTarget)
    }
  }

  return (
    <header className="lk-tabs" ref={bar}>
      <Link to="/home" className="lk-tabs__brand" aria-hidden="true" tabIndex={-1} title="Home">
        <Wordmark size={17} className="lk-tabs__wordmark" />
        <VMark size={20} className="lk-tabs__vmark" />
      </Link>
      <nav aria-label="Main" className="flex min-w-0 flex-1">
        <ul className="lk-tabs__list">
          <li className={`lk-tab is-home${current === HOME_TAB ? ' is-current' : ''}`}>
            <Link
              to={placeOf(HOME_TAB, '/home', current)}
              className="lk-tab__main"
              data-tab={HOME_TAB}
              aria-current={current === HOME_TAB ? 'page' : undefined}
              aria-label="Home"
              aria-keyshortcuts={tabKeyShortcuts(0, standalone)}
              title={`Home (${tabShortcut(0, standalone)})`}
            >
              <kbd>{tabShortcut(0, standalone)}</kbd>
              <House size={16} aria-hidden="true" />
              <span className="lk-tab__name">Home</span>
            </Link>
            {totals.needs ? (
              <Link
                to={homeHref({ v: 'needs' })}
                className="lk-badge is-wait"
                aria-label={`${totals.needs} ${totals.needs === 1 ? 'needs' : 'need'} you`}
              >
                <Glyph kind="wait" />
                {totals.needs}
              </Link>
            ) : null}
            {totals.running ? (
              <Link
                to={homeHref({ v: 'running' })}
                className="lk-badge is-run"
                aria-label={`${totals.running} ${totals.running === 1 ? 'agent' : 'agents'} running`}
              >
                <Glyph kind="run" />
                {totals.running}
              </Link>
            ) : null}
          </li>
          {lockChip}
          <li className="lk-tabs__sep" aria-hidden="true" />
          {shown.map((t, i) => {
            const on = t.id === current
            const key = tabShortcut(i + 1, standalone)
            return (
              <li
                key={t.id}
                className={`lk-tab${on ? ' is-current' : ''}${drag?.id === t.id ? ' is-dragging' : ''}${drag && drag.over === i && drag.id !== t.id ? ' is-drop' : ''}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', t.id)
                  setDrag({ id: t.id })
                }}
                onDragOver={(e) => {
                  if (!drag) return
                  e.preventDefault()
                  if (drag.over !== i) setDrag({ ...drag, over: i })
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  if (drag && drag.id !== t.id) onMove(drag.id, i)
                  setDrag(null)
                }}
                onDragEnd={() => setDrag(null)}
              >
                <Link
                  to={placeOf(t.id, projectHref(t.id), current)}
                  className="lk-tab__main"
                  data-tab={t.id}
                  aria-current={on ? 'page' : undefined}
                  aria-label={t.name}
                  aria-keyshortcuts={tabKeyShortcuts(i + 1, standalone)}
                  title={`${t.name} (${key})`}
                  onKeyDown={(e) => onTabKey(e, t.id, i)}
                  onContextMenu={(e) => {
                    e.preventDefault()
                    openMenu(t.id, e.currentTarget, e)
                  }}
                >
                  <kbd>{key}</kbd>
                  <span className="lk-tab__name">{t.name}</span>
                </Link>
                {t.needs ? (
                  <Link
                    to={homeHref({ v: 'needs', project: t.id })}
                    className="lk-badge is-wait"
                    aria-label={`${t.needs} ${t.needs === 1 ? 'needs' : 'need'} you in ${t.name}`}
                  >
                    <Glyph kind="wait" />
                    {t.needs}
                  </Link>
                ) : null}
                {t.running ? (
                  <Link
                    to={homeHref({ v: 'running', project: t.id })}
                    className="lk-badge is-run"
                    aria-label={`${t.running} ${t.running === 1 ? 'agent' : 'agents'} running in ${t.name}`}
                  >
                    <Glyph kind="run" />
                    {t.running}
                  </Link>
                ) : null}
              </li>
            )
          })}
        </ul>
        {extra.length || unpinned.length ? (
          <button
            type="button"
            className="lk-tabs__tool is-solid"
            aria-label={`More projects (${extra.length + unpinned.length})`}
            aria-expanded={!!overflow}
            aria-haspopup="menu"
            onClick={(e) => {
              const host = bar.current?.getBoundingClientRect()
              const r = e.currentTarget.getBoundingClientRect()
              setMenu(null)
              setOverflow((o) =>
                o ? null : { x: r.left - (host?.left ?? 0), y: r.bottom - (host?.top ?? 0) + 4 },
              )
            }}
          >
            <MoreHorizontal size={16} aria-hidden="true" />
          </button>
        ) : null}
        <button
          type="button"
          className="lk-tabs__tool"
          aria-label="Add a project"
          title="Add a project"
          onClick={onAdd}
        >
          <Plus size={16} aria-hidden="true" />
        </button>
      </nav>
      <div className="lk-tabs__global">
        {callSlot}
        <button
          type="button"
          className="lk-gbtn lk-search"
          aria-label="Search or act"
          aria-keyshortcuts="Meta+K Control+K"
          onClick={onSearch}
        >
          <Search size={15} aria-hidden="true" />
          <span>Search or act</span>
          <kbd>{shortcutLabel('global.palette')}</kbd>
        </button>
        <Link
          to="/settings"
          className={`lk-settings${current === SETTINGS_TAB ? ' is-current' : ''}`}
          aria-label="Settings"
          aria-current={current === SETTINGS_TAB ? 'page' : undefined}
          title="Settings (g ,)"
        >
          <Settings size={18} aria-hidden="true" />
        </Link>
      </div>
      {menu ? (
        <TabMenu
          at={menu}
          onClose={() => setMenu(null)}
          items={[
            ...(tabs.findIndex((t) => t.id === menu.id) > 0
              ? [
                  {
                    label: 'Move left',
                    run: () => onMove(menu.id, tabs.findIndex((t) => t.id === menu.id) - 1),
                  },
                ]
              : []),
            ...(tabs.findIndex((t) => t.id === menu.id) < tabs.length - 1
              ? [
                  {
                    label: 'Move right',
                    run: () => onMove(menu.id, tabs.findIndex((t) => t.id === menu.id) + 1),
                  },
                ]
              : []),
            ...tabs
              .map((_, n) => n)
              .filter((n) => n !== tabs.findIndex((t) => t.id === menu.id) && n < TAB_KEYS)
              .map((n) => ({ label: `Move to position ${n + 1}`, run: () => onMove(menu.id, n) })),
            { label: 'Unpin', run: () => onUnpin(menu.id), sep: true },
            {
              label: 'Project settings',
              to: projectHref(menu.id, 'settings'),
            },
            {
              label: 'Archive…',
              run: () => setArchive(tabs.find((t) => t.id === menu.id) ?? null),
            },
          ]}
        />
      ) : null}
      {overflow ? (
        <TabMenu
          at={overflow}
          onClose={() => setOverflow(null)}
          label="More projects"
          items={[
            ...extra.map((t) => ({ label: t.name, to: projectHref(t.id) })),
            ...unpinned.map((p) => ({ label: `Pin ${p.name}`, run: () => onPin(p.id) })),
          ]}
        />
      ) : null}
      <Sheet
        open={!!archive}
        onClose={() => setArchive(null)}
        placement="center"
        title={`Archive ${archive?.name ?? ''}?`}
        subtitle="It leaves the tab strip. Its workstreams, tasks and history stay readable."
        actions={
          <>
            <Button variant="ghost" onClick={() => setArchive(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (archive) onArchive(archive.id)
                setArchive(null)
              }}
            >
              Archive
            </Button>
          </>
        }
      >
        <p className="m-0 text-[14px] text-text-2">
          Running agents keep running. Unarchive it from Home › Projects.
        </p>
      </Sheet>
    </header>
  )
}

interface MenuItem {
  label: string
  run?: () => void
  to?: string
  sep?: boolean
}

function TabMenu({
  at,
  items,
  onClose,
  label = 'Tab actions',
}: {
  at: { x: number; y: number }
  items: MenuItem[]
  onClose: () => void
  label?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    const onClose = () => close.current()
    const prev = document.activeElement as HTMLElement | null
    ref.current?.querySelector<HTMLElement>('button, a')?.focus()
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        prev?.focus()
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const all = [...(ref.current?.querySelectorAll<HTMLElement>('button, a') ?? [])]
        const i = all.indexOf(document.activeElement as HTMLElement)
        all[(i + (e.key === 'ArrowDown' ? 1 : all.length - 1)) % all.length]?.focus()
      }
    }
    addEventListener('pointerdown', onDown)
    addEventListener('keydown', onKey)
    return () => {
      removeEventListener('pointerdown', onDown)
      removeEventListener('keydown', onKey)
    }
  }, [])
  return (
    <div
      ref={ref}
      className="lk-menu"
      role="menu"
      aria-label={label}
      style={{ left: Math.max(8, at.x), top: at.y }}
    >
      {items.map((m) =>
        m.to ? (
          <Link key={m.label} to={m.to} role="menuitem" onClick={onClose}>
            {m.label}
          </Link>
        ) : (
          <div key={m.label} className="contents">
            {m.sep ? <hr /> : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                m.run?.()
                onClose()
              }}
            >
              {m.label}
            </button>
          </div>
        ),
      )}
    </div>
  )
}
